<template>
  <div class="page">
    <div class="page-header">
      <h2 class="page-title">下载记录</h2>
    </div>

    <div class="table-card">
      <el-table
        v-loading="loading"
        element-loading-text="加载中..."
        element-loading-background="rgba(0, 0, 0, 0.8)"
        element-loading-svg-view-box="-10, -10, 80, 80"
        :element-loading-svg="loadingSvg"
        :data="downloadRecords"
        class="data-table"
        empty-text="暂无下载记录"
      >
        <el-table-column label="#" width="60" align="center">
          <template #default="scope">
            <span class="row-num">{{ (currentPage - 1) * pageSize + scope.$index + 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="歌曲名称" min-width="160" show-overflow-tooltip>
          <template #default="scope">
            <span class="cell-text">{{ scope.row.songName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="下载方式" width="100" align="center">
          <template #default="scope">
            <span class="cell-tag" :class="scope.row.downloadType === '00' ? 'direct' : 'cloud'">
              {{ scope.row.downloadType === '00' ? '直接下载' : '网盘下载' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="制作人" min-width="120">
          <template #default="scope">
            <span class="cell-link" @click="handleArtistClick(scope.row)">{{ scope.row.singerName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="曲风" min-width="100" show-overflow-tooltip>
          <template #default="scope">
            <span class="cell-meta">{{ scope.row.songStyleName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="套曲" min-width="100" show-overflow-tooltip>
          <template #default="scope">
            <span class="cell-meta">{{ scope.row.musicalCycleName || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="BPM" width="70" align="center">
          <template #default="scope">
            <span class="cell-meta">{{ scope.row.songBpm || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="false" label="时长" width="70" align="center">
          <template #default="scope">
            <span class="cell-meta">{{ scope.row.totalAudioDuration || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="大小" width="80" align="center">
          <template #default="scope">
            <span class="cell-meta">{{ formatFileSize(scope.row.fileSize) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="格式" width="70" align="center">
          <template #default="scope">
            <span class="cell-meta">{{ scope.row.fileSuffix || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="调性" width="80" align="center">
          <template #default="scope">
            <span class="cell-meta">{{ scope.row.songTonality || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="下载时间" width="170">
          <template #default="scope">
            <span class="cell-time">{{ scope.row.downloadTime }}</span>
          </template>
        </el-table-column>
      </el-table>

      <CustomPagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-size-options="[10, 20, 30, 50]"
        @change="handlePaginationChange"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { HttpManager } from '@/api'
import { ElMessage } from 'element-plus'
import router from '@/router'
import CustomPagination from '@/components/common/CustomPagination.vue'

const downloadRecords = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const loading = ref(false)

const loadingSvg = `
  <svg xmlns="http://www.w3.org/2000/svg">
    <image href="/20250706Logo.png" width="45" height="45" x="7" y="7" preserveAspectRatio="xMidYMid meet"/>
  </svg>
`;

const fetchDownloadRecords = async () => {
  loading.value = true
  try {
    const response = await HttpManager.getLoginDownloadRecordPage({
      page: currentPage.value,
      size: pageSize.value
    })
    downloadRecords.value = response.data.data
    total.value = response.data.total
  } catch (error) {
    console.error('获取下载记录失败:', error)
  } finally {
    loading.value = false
  }
}

const handlePaginationChange = (page: number, size: number) => {
  currentPage.value = page
  pageSize.value = size
  fetchDownloadRecords()
}

const formatFileSize = (size: number) => {
  if (!size) return '-'
  if (size < 1024) return size + 'B'
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + 'KB'
  return (size / (1024 * 1024)).toFixed(1) + 'MB'
}

const handleArtistClick = (artist: any) => {
  if (!artist?.singerId) {
    ElMessage.warning('制作人信息不完整')
    return
  }
  router.push({ path: `/singer-detail/${artist.singerId}`, query: { type: 'artist' } })
}

onMounted(() => { fetchDownloadRecords() })
</script>

<style lang="scss" scoped>
.page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 100px;
  min-height: 100vh;
  background: #262626;
}

.page-header { margin-bottom: 28px; }

.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  margin: 0;
}

.table-card {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px;
  padding: 20px;
  overflow-x: auto;
}

/* table */
:deep(.data-table) {
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: transparent;

  &::before { height: 0; }

  .el-table__header th {
    background: transparent;
    color: rgba(255,255,255,0.4);
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    padding: 10px 0;
  }

  .el-table__body td {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    padding: 12px 0;
  }

  .el-table__row:hover > td {
    background: rgba(255,255,255,0.02) !important;
  }
}

.row-num { color: rgba(255,255,255,0.25); font-size: 13px; }
.cell-text { color: #fff; font-size: 14px; }
.cell-time { color: rgba(255,255,255,0.4); font-size: 13px; }
.cell-meta { color: rgba(255,255,255,0.5); font-size: 13px; }

.cell-link {
  color: rgba(255,255,255,0.7);
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: #1ed760; }
}

.cell-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  &.direct { background: rgba(30,215,96,0.1); color: #1ed760; }
  &.cloud { background: rgba(100,149,237,0.1); color: #6495ed; }
}

:deep(.el-loading-mask) { background: rgba(38,38,38,0.85) !important; }
:deep(.el-loading-spinner .path) { stroke: #1ed760; }
:deep(.el-loading-text) { color: rgba(255,255,255,0.4); }
</style>
